/**
 * Late-binding registry for feature-owned DATA LOADERS the shell consumes
 * (design 5.4; round 9 revision): the registry holds PLAIN async
 * functions, never React Hooks — the platform owns one stable Hook whose
 * useState/useEffect always execute, so the hook count can never change
 * between renders regardless of registration timing (Rules of Hooks).
 * Unregistered loader = safe empty state; a registration after first
 * render is picked up on the next refresh cycle.
 *
 * Reads go through the shared TanStack Query client (platform/query), so
 * every component on a page that asks for the integration summary shares
 * one request, one 60-second freshness window, and no polling in a hidden
 * tab — instead of each mounting its own fetch, focus listener and timer.
 */

import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

let integrationSummaryLoader = null

/**
 * Bounded freshness for the configured signal. Menus, personal credentials,
 * knowledge corpora and the MCP surface are gated on whether an integration is
 * configured, so configuring one has to take visible effect without a reload of
 * unbounded age. Focus refresh alone is not enough: a tab left open and focused
 * would never re-read. One minute is short enough that enabling an integration
 * feels immediate, long enough to cost one small request per idle minute.
 */
export const INTEGRATION_SUMMARY_MAX_AGE_MS = 60_000

export const INTEGRATION_SUMMARY_QUERY_KEY = ['platform', 'integration-summary']

/**
 * Shared Settings reads (fast-console-pages item settings-shared-primitives):
 * the 1Password connection list nearly every credential tab and instance
 * page needs, the slim appliance picker options, and the self-service
 * catalog. One request per key per freshness window, shared by every
 * component on the page — instead of each tab and modal mounting its own
 * fetch. Same loader-registry pattern as the integration summary above.
 */
export const OP_CONNECTIONS_QUERY_KEY = ['settings', 'op-connections']
export const APPLIANCE_OPTIONS_QUERY_KEY = ['settings', 'appliance-options']
export const HOST_AGENT_OPTIONS_QUERY_KEY = ['settings', 'host-agent-options']
export const SELF_SERVICE_INTEGRATIONS_QUERY_KEY = ['settings', 'self-service-integrations']

export { isIntegrationConfigured } from '../../../shared/integrationConfigured'

/**
 * The application composition root registers a plain async
 * () => normalizedSummary (throws on failure). NOT a Hook.
 */
export function registerIntegrationSummaryLoader(fn) {
  integrationSummaryLoader = fn
}

export function useIntegrationSummaryQuery(options = {}) {
  return useQuery({
    queryKey: INTEGRATION_SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const loader = integrationSummaryLoader
      // No provider composed (yet): settle into the empty state; a later
      // refresh (focus, manual, invalidation) re-reads the registry.
      if (!loader) return {}
      return loader()
    },
    staleTime: INTEGRATION_SUMMARY_MAX_AGE_MS,
    refetchInterval: INTEGRATION_SUMMARY_MAX_AGE_MS,
    ...options,
  })
}

export function useIntegrationSummaryRegistered() {
  const { data, isPending, error, refetch } = useIntegrationSummaryQuery()
  return {
    // {} is the safe empty: consumers use summary[key]?.field access.
    summary: data ?? {},
    loading: isPending,
    error: error ? (error.response?.data?.error || error.message || 'Failed to load integration summary') : null,
    refresh: refetch,
  }
}

/**
 * Ask the shared summary to re-read after an integration was saved, so the
 * menus and gates notice the new configuration without waiting out the
 * freshness window. A catalog toggle also moves tabs in and out of the
 * settings nav, so the self-service catalog re-reads too.
 */
export function useInvalidateIntegrationSummary() {
  const queryClient = useQueryClient()
  return useCallback(
    () => Promise.all([
      queryClient.invalidateQueries({ queryKey: INTEGRATION_SUMMARY_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: SELF_SERVICE_INTEGRATIONS_QUERY_KEY }),
    ]),
    [queryClient],
  )
}

let opConnectionsLoader = null
let applianceOptionsLoader = null
let hostAgentOptionsLoader = null

/**
 * The application composition root registers plain async () => normalized
 * readers (throws on failure). NOT Hooks. The readers are registered with
 * lazy imports so the shell bundle never eagerly pulls in the settings or
 * queue route graphs.
 */
export function registerOpConnectionsLoader(fn) {
  opConnectionsLoader = fn
}

export function registerApplianceOptionsLoader(fn) {
  applianceOptionsLoader = fn
}

export function registerHostAgentOptionsLoader(fn) {
  hostAgentOptionsLoader = fn
}

/**
 * Every 1Password credential branch and instance modal shares one cached
 * connection list. `enabled` gates the read: pass true only when a
 * 1Password branch or modal is shown, so hidden branches cost nothing.
 * `data` is the normalized connections array ([] while unloaded).
 */
export function useOpConnectionsQuery(options = {}) {
  return useQuery({
    queryKey: OP_CONNECTIONS_QUERY_KEY,
    queryFn: async () => {
      const loader = opConnectionsLoader
      // No provider composed (yet): settle into the empty state; a later
      // refresh (focus, manual, invalidation) re-reads the registry.
      if (!loader) return []
      return loader()
    },
    staleTime: INTEGRATION_SUMMARY_MAX_AGE_MS,
    ...options,
  })
}

/**
 * The appliance picker shared by the 1Password modal and every instance
 * page: the slim GET /api/agents/options?role=appliance list, never the
 * unbounded full agent rows. `enabled` gates the read: pass true only when
 * the picker's select is shown. `data` is [{ id, name }] ([] unloaded).
 */
export function useApplianceOptionsQuery(options = {}) {
  return useQuery({
    queryKey: APPLIANCE_OPTIONS_QUERY_KEY,
    queryFn: async () => {
      const loader = applianceOptionsLoader
      if (!loader) return []
      return loader()
    },
    staleTime: INTEGRATION_SUMMARY_MAX_AGE_MS,
    ...options,
  })
}

/**
 * The Linux host-agent picker (Kubernetes setup through an agent on a
 * control-plane node): the slim GET /api/agents/options?role=agent&os_type=linux
 * list. `enabled` gates the read: pass true only when the picker's select is
 * shown. `data` is [{ id, name }] ([] unloaded).
 */
export function useHostAgentOptionsQuery(options = {}) {
  return useQuery({
    queryKey: HOST_AGENT_OPTIONS_QUERY_KEY,
    queryFn: async () => {
      const loader = hostAgentOptionsLoader
      if (!loader) return []
      return loader()
    },
    staleTime: INTEGRATION_SUMMARY_MAX_AGE_MS,
    ...options,
  })
}

/**
 * Re-read the shared connection list after a 1Password connection was
 * created, updated, deleted or otherwise changed, so every credential tab
 * and instance modal notices without waiting out the freshness window.
 */
export function useInvalidateOpConnections() {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: OP_CONNECTIONS_QUERY_KEY }),
    [queryClient],
  )
}
